import { Injectable } from '@angular/core';
import { PromptTemplateType } from '@app/types/PromptTemplate';
import { IUserPromptOverrides } from '@common/models/IUserPromptOverrides';
import { ApiService } from 'quangular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PromptService {
    constructor(private readonly apiService: ApiService) {}
    /**
     * Fetch available prompt templates from backend.
     * Now returns array of { name, description }.
     * The label for UI display should be provided by the backend or use the name as fallback.
     */
    fetchPromptTemplates(): Observable<
        { name: string; label: string; description: string }[]
    > {
        return this.apiService
            .get<PromptTemplateType[]>(
                `prompts/templates`
            )
            .pipe(
                map((prompts) =>
                    prompts.map((p) => ({
                        name: p.name,
                        label: p.label || p.name,
                        description: p.description || '',
                    }))
                )
            );
    }

    /**
     * Set the active prompt template by filename.
     */
    /**
     * Deprecated: use setActivePromptForUser instead.
     */
    setActivePromptTemplate(promptName: string): Observable<{ activePrompt: string }> {
        return this.apiService.post(`prompts/user/prompt-overrides/set-active`, { promptName });
    }

    /**
     * Get user prompt overrides from MongoDB.
     */
    getUserPromptOverrides(): Observable<IUserPromptOverrides> {
        return this.apiService.get<IUserPromptOverrides>(
            `prompts/user/prompt-overrides`
        );
    }

    /**
     * Save user prompt overrides to MongoDB.
     * Deprecated: use saveUserPromptOverride instead.
     */
    saveUserPromptOverrides(
        promptOverrides: IUserPromptOverrides
    ): Observable<IUserPromptOverrides> {
        return this.apiService.put(
            `prompts/user/prompt-overrides`,
            promptOverrides
        );
    }

    /**
     * Save or update a user's prompt override (content) via new backend endpoint.
     * Only to be used by the QIQ Prompt Library.
     */
    saveUserPromptOverride(
        promptName: string,
        content: string
    ): Observable<{ overrides: any[]; activePrompt: string }> {
        return this.apiService.post(`prompts/user/prompt-overrides/save`, {
            promptName,
            content,
        });
    }

    /**
     * Set the active prompt for the user (selection only, no content change).
     * If chatId is provided, it updates the chat-specific prompt.
     * Otherwise, it updates the user's preferred prompt.
     * @param promptName The name of the prompt template.
     * @param chatId Optional chat ID to update chat-specific prompt.
     * @returns Observable with the active prompt name.
     */
    setActivePromptForUser(
        promptName: string,
        chatId?: string
    ): Observable<{ activePrompt: string }> {
        const body: any = { promptName };
        if (chatId) {
            body.chatId = chatId;
        }
        return this.apiService.post(
            `prompts/user/prompt-overrides/set-active`,
            body
        ) as Observable<{ activePrompt: string }>;
    }

    /**
     * Fetch the content of a system prompt template.
     * @param promptName The name of the prompt template.
     * @returns The content of the prompt template.
     */
    fetchPromptContent(promptName: string): Observable<string> {
        return this.apiService.get<string>(`prompts/content/${promptName}`);
    }
    /**
     * Reset a user prompt override to its default value.
     * @param promptName The name of the prompt template to reset.
     * @returns The reset prompt content as a string.
     */
    resetPromptOverride(promptName: string): Observable<{ content: string }> {
        return this.apiService.post(`prompts/user/prompt-overrides/reset`, {
            promptName,
        });
    }
}
